import React, { useState, useEffect, useMemo } from "react";
import { Text, Icon } from 'basis';
import { connect } from "react-redux";
import Button from '@mui/material/Button';

import Question from './Question';
import LinearProgressWithLabel from "./LinearProgressWithLabel";
import Divider from "./Divider";
import { ReactComponent as CompletedIcon } from '../images/completed.svg';
import { ReactComponent as FailedIcon } from '../images/failed.svg';
import { recordClick, fetchQuiz, submitAttempt } from '../functions/TrainingFunctions';
import { arraysEqual, randomizeArray } from "../functions/utils";
import { getRequiredLinks, getTrainingModule } from '../functions/trainingModules';

const mapStateToProps = (state) => ({
    authDetails: state.user.authDetails,
    clicked: state.training.clicked
});

const mapDispatchToProps = { recordClick, fetchQuiz, submitAttempt };

// TrainingModule renders one self-contained training section (its required links,
// the gated quiz, and its own submit / success / fail state) for a single module.
// Quiz pool and submit result are kept in LOCAL state (via fetchQuiz/submitAttempt)
// so multiple sections can live on one page without clobbering shared redux state.
function TrainingModule(props) {
    const { module, title, width, alreadyCompleted, merchantId } = props;
    const { authDetails, clicked } = props;
    const { fetchQuiz, submitAttempt, recordClick } = props;

    const [quiz, setQuiz] = useState(null);
    const [quizError, setQuizError] = useState(null);
    const [questionSet, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [percentCompleted, setPercentCompleted] = useState(0);
    const [isSuccess, setSuccess] = useState(false);
    const [isFailed, setFailed] = useState(false);

    const questions = useMemo(() => (quiz && quiz.questions ? quiz.questions : {}), [quiz]);
    const Q_PER_PAGE = quiz && quiz.questionsPerAttempt ? quiz.questionsPerAttempt : 5;
    const region = getTrainingModule(module).region || '';
    const requiredLinks = getRequiredLinks(module);
    const allLinksClicked = requiredLinks.length === 0 || requiredLinks.every((link) => clicked && clicked[link.key]);

    //pre-tick when UMS reports this training as already complete and still valid
    useEffect(() => {
        if (alreadyCompleted){
            setSuccess(true);
        }
    }, [alreadyCompleted]);

    //fetch the quiz pool (questions only) for this module
    useEffect(() => {
        if (authDetails && authDetails.authToken && !quiz && !quizError){
            fetchQuiz(authDetails.authToken, module)
                .then((data) => { if (data) setQuiz(data); })
                .catch(() => setQuizError('error'));
        }
    }, [authDetails, quiz, quizError, module, fetchQuiz]);

    //once the pool has loaded, draw a fresh question set. Answers always start
    //blank - previously saved answers are intentionally not restored (no pre-fill).
    useEffect(() => {
        if (!quiz || questionSet.length !== 0){
            return;
        }
        const drawn = randomizeArray(questions).slice(0, Q_PER_PAGE).sort((a, b) => a - b);
        const prog = {};
        drawn.forEach((qId) => { prog[qId] = ''; });
        setQuestions(drawn);
        setAnswers(prog);
    }, [quiz, questionSet.length, module, questions, Q_PER_PAGE]);

    //recompute the progress bar as answers change
    useEffect(() => {
        let answered = 0;
        Object.values(answers).forEach((answer) => { if (answer !== '') answered++; });
        setPercentCompleted((answered / Q_PER_PAGE) * 100);
    }, [answers, Q_PER_PAGE]);

    const handleAnswer = (key, val) => {
        const currentAnswers = { ...answers, [key]: val };
        setAnswers(currentAnswers);
    };

    const onSubmit = () => {
        submitAttempt(authDetails.authToken, { name: module, data: { quiz: answers }, region, merchantId })
            .then((res) => {
                if (!res) return;
                if (parseInt(res.score) === 100){
                    setSuccess(true);
                } else {
                    setFailed(true);
                }
            })
            .catch(() => {});
    };

    const onTryAgain = () => {
        const drawn = randomizeArray(questions).slice(0, Q_PER_PAGE).sort((a, b) => a - b);
        const prog = {};
        drawn.forEach((qId) => { prog[qId] = ''; });
        setQuestions(drawn);
        setAnswers(prog);
        setPercentCompleted(0);
        setFailed(false);
        setSuccess(false);
    };

    const renderSuccess = (
        <div style={{display: 'flex', alignItems: 'center', flexDirection: 'column'}}>
            <CompletedIcon/>
            <div style={{marginTop:20}}>
                <Text as="h5" textStyle="heading5">Congratulations!</Text>
            </div>
            <div style={{marginTop: 10}}>
                <Text textStyle="subtitle2">You've completed this training module</Text>
            </div>
        </div>
    );

    const renderFail = (
        <div style={{display: 'flex', alignItems: 'center', flexDirection: 'column'}}>
            <FailedIcon/>
            <div style={{marginTop:30}}>
                <Text as="h5" textStyle="heading5">You'll need to try again</Text>
            </div>
            <div style={{marginTop: 10}}>
                <Text textStyle="subtitle2">Unfortunately, you did not pass this training module.</Text>
            </div>
            <div style={{marginTop: 10}}>
                <Text textStyle="subtitle2">
                    In order to meet compliance for <strong>Latitude Checkout</strong>, you'll need to repeat this module.
                </Text>
            </div>
            <Button variant="contained"
                style={{width: 200, height: 50, backgroundColor: '#0061EE', textTransform: 'none', fontFamily:'Roboto', fontWeight:500, fontSize:18, marginTop:30}}
                onClick={onTryAgain}>
                Try again
            </Button>
        </div>
    );

    const renderQuiz = (
        <React.Fragment>
            <LinearProgressWithLabel variant="determinate" value={percentCompleted} direction={width > 750 ? 'row' : 'column'} style={{marginRight: width > 750 ? 25 : 0, height: 8, borderRadius: 8, backgroundColor: '#E5E5E5'}}/>
            <Divider style={{marginBlock:32}}/>
            {
                quiz &&
                arraysEqual(Object.keys(answers), questionSet) &&
                questionSet.map((key, idx) => {
                    return <Question
                        key={idx}
                        style={{marginBottom:40}}
                        question={questions[key].question}
                        choice={questions[key].choice}
                        multiple={questions[key].multiple}
                        answer={answers[key]}
                        qId={key}
                        qNumber={idx+1}
                        onChange={(val) => handleAnswer(key, val)}
                    />
                })
            }
            <div style={{display:'flex', flexDirection: 'row', justifyContent: width > 750 ? 'flex-start' : 'center'}}>
                <Button variant="contained"
                    disabled={percentCompleted<100}
                    style={{width: 200, height: 50, backgroundColor: percentCompleted < 100 ? null : '#0061EE', textTransform: 'none', fontFamily:'Roboto', fontWeight:500, fontSize:18 }}
                    onClick={onSubmit}>
                    Submit
                </Button>
            </div>
        </React.Fragment>
    );

    return (
        <div style={{display:'flex', flexDirection: 'column', width: width > 750 ? '60%' : '75%', alignSelf: 'center', marginBottom: 24}}>
            {
                title &&
                <div style={{marginBottom: 24}}>
                    <Text as="h4" textStyle="heading4" color="primary.blue.t100">{title}</Text>
                </div>
            }
            {
                !isSuccess && !isFailed &&
                requiredLinks.map((link) => {
                    const isOpened = !!(clicked && clicked[link.key]);
                    return (
                    <div key={link.key} style={{background: '#FFFFFF', width: '100%', boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)', borderRadius: 10, padding:32, marginBottom:32, boxSizing: 'border-box'}}>
                        <div style={{display:'flex', alignItems:'center', gap:8}}>
                            <Text as="h5" textStyle="heading5" >{link.linkTitle}</Text>
                            {isOpened && <Icon name="tick" color="conditional.positive.graphics" />}
                        </div>
                        <div style={{marginTop:20}}>
                            <Text>
                                {link.linkBody}
                            </Text>
                        </div>
                        <Button variant="outlined"
                            style={{width: width > 375 ? 300 : width > 280 ? 250 : 200, textTransform: 'none', fontFamily:'Roboto', fontWeight:500, fontSize:18, marginTop:10, paddingBlock: 5 }}
                            onClick={()=>{
                                window.open(link.contentUrl, '_blank', 'noopener,noreferrer');
                                if (!isOpened && authDetails && authDetails.authToken){
                                    recordClick(authDetails.authToken, link.key);
                                }
                            }}>
                            {link.linkButtonLabel}
                        </Button>
                    </div>
                    );
                })
            }
            {
                !isSuccess && !isFailed && requiredLinks.length > 0 &&
                <div style={{background: '#FFFFFF', width: '100%', boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)', borderRadius: 10, padding:32, marginBottom:32, boxSizing: 'border-box'}}>
                    <div style={{display:'flex', alignItems:'center', gap:8}}>
                        {!allLinksClicked && <Icon name="lock" color="grey.t75" />}
                        <Text>
                            {
                                allLinksClicked
                                    ? 'Once you have completed the training, please complete the questionnaire below. All questions must be answered correctly before you can proceed.'
                                    : 'Please open all of the training links above to unlock the questionnaire.'
                            }
                        </Text>
                    </div>
                </div>
            }
            {
                (isSuccess || isFailed || allLinksClicked) &&
                <div style={{background: '#FFFFFF', width: '100%', boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)', borderRadius: 10, padding:32, boxSizing: 'border-box'}}>
                    {isSuccess ? renderSuccess : isFailed ? renderFail : renderQuiz}
                </div>
            }
        </div>
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(TrainingModule);
